import { requestWithToken as request } from "./api";

const BASE = "/financial-reports";

function buildQuery(params) {
    const entries = Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
    );
    if (entries.length === 0) return "";
    const qs = entries
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    return `?${qs}`;
}

// Generic endpoints — three resource-oriented calls cover every tab.
// `config` (opcional) se pasa al request de axios — mismo patrón que
// saveFintocUserHolder. Hoy lo usan los stores take-latest para viajar el
// `signal` de AbortController (inerte en axios 0.21; abort real en 1.x).
export function getCategorySummary(category, endDate, config = {}) {
    return request.get(`${BASE}/${category}/summary/${buildQuery({ end_date: endDate })}`, config);
}

export function getCategoryBreakdown(category, initDate, endDate, groupBy = "cuentas", period = "month", config = {}) {
    return request.get(
        `${BASE}/${category}/breakdown/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            group_by: groupBy,
            period,
        })}`,
        config
    );
}

export function getCategoryCharts(category, initDate, endDate, period = "month", topN = 7, groupBy = "clientes") {
    return request.get(
        `${BASE}/${category}/charts/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            period,
            top_n: topN,
            group_by: groupBy,
        })}`
    );
}

export function getCategoryComparison(category, endDate, years, groupBy = null, item = null) {
    return request.get(
        `${BASE}/${category}/comparison/${buildQuery({
            end_date: endDate,
            years: Array.isArray(years) ? years.join(",") : years,
            group_by: groupBy,
            item,
        })}`
    );
}

export function getResultStateByPeriod(period, initDate, endDate) {
    return request.get(
        `${BASE}/state/${buildQuery({ period, init_date: initDate, end_date: endDate })}`
    );
}

// Backwards-compatible wrappers — kept so the 7 Vuex modules don't need to change.
// Each one is a one-line delegation to the generic call above.

export function getSalesByLevel3(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("sales", initDate, endDate, groupBy, period);
}

export function getSalesCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("sales", initDate, endDate, period, topN, groupBy);
}

export function getSalesComparison(endDate, years, groupBy, item) {
    return getCategoryComparison("sales", endDate, years, groupBy, item);
}

export function getExpensesByLevels(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("expenses", initDate, endDate, groupBy, period);
}

export function getExpensesCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("expenses", initDate, endDate, period, topN, groupBy);
}

export function getExpensesComparison(endDate, years, groupBy = null, item = null) {
    return getCategoryComparison("expenses", endDate, years, groupBy, item);
}

// Expenses analysis modal — drill-down de un mes de Gastos. Mismo contrato de
// query que getCostsAnalysis; el backend devuelve statement (con eficiencia) +
// counterparts_ranking.
export function getExpensesAnalysis(month, account = null, groupBy = null, selectedItems = null, accountLevel = null) {
    return request.get(
        `${BASE}/expenses/analysis/${buildQuery({
            month,
            account,
            account_level: accountLevel,
            group_by: groupBy,
            attribute_filters: serializeAttributeFilters(selectedItems),
        })}`
    );
}

export function getCostsByLevels(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("costs", initDate, endDate, groupBy, period);
}

export function getCostsCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("costs", initDate, endDate, period, topN, groupBy);
}

export function getCostsComparison(endDate, years, groupBy = null, item = null) {
    return getCategoryComparison("costs", endDate, years, groupBy, item);
}

export function getAssetsByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("assets", initDate, endDate, groupBy, period, config);
}

export function getAssetsSummaryCards(endDate, config) {
    return getCategorySummary("assets", endDate, config);
}

export function getLiabilitiesByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("liabilities", initDate, endDate, groupBy, period, config);
}

export function getLiabilitiesSummaryCards(endDate, config) {
    return getCategorySummary("liabilities", endDate, config);
}

export function getEquityByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("equity", initDate, endDate, groupBy, period, config);
}

export function getEquitySummaryCards(endDate, config) {
    return getCategorySummary("equity", endDate, config);
}

// Resumen de Balance: breakdown de los tres nivel-1 (Activos/Pasivos/Patrimonio)
// en una respuesta (`groups`). Siempre por cuentas — el Resumen no agrupa por
// contraparte/categoría, así que no expone `groupBy`.
export function getBalanceByLevels(initDate, endDate, period) {
    return getCategoryBreakdown("balance", initDate, endDate, "cuentas", period);
}

export function getNonOperationalByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("non-operational", initDate, endDate, groupBy, period, config);
}

export function getNonOperationalSummaryCards(endDate, config) {
    return getCategorySummary("non-operational", endDate, config);
}

export function getProfitabilitySummaryCards(endDate) {
    return getCategorySummary("profitability", endDate);
}

// Sales analysis modal — drill-down de un mes específico.
// `selectedItems` es un dict { tipo: [name, ...] }; se envía JSON-encoded
// en el query param `attribute_filters`. El backend descarta entradas con
// listas vacías, así que filtramos client-side antes de serializar para
// no inflar la URL.
//
// `groupBy` acepta string (legacy: una sola categoría) o array (varias
// categorías para mostrar múltiples distribuciones en simultáneo). El
// backend splittea el query param por comas, así que serializamos así.
export function getSalesAnalysis(month, account = null, groupBy = null, selectedItems = null) {
    const groupByParam = Array.isArray(groupBy) ? groupBy.join(",") : groupBy;
    return request.get(
        `${BASE}/sales/analysis/${buildQuery({
            month,
            account,
            group_by: groupByParam,
            attribute_filters: serializeAttributeFilters(selectedItems),
        })}`
    );
}

// Costs analysis modal — drill-down de un mes de Costos. Mismo contrato de
// query que getSalesAnalysis (month/account/group_by/attribute_filters); el
// backend devuelve un payload distinto (statement + counterparts_ranking).
export function getCostsAnalysis(month, account = null, groupBy = null, selectedItems = null, accountLevel = null) {
    return request.get(
        `${BASE}/costs/analysis/${buildQuery({
            month,
            account,
            account_level: accountLevel,
            group_by: groupBy,
            attribute_filters: serializeAttributeFilters(selectedItems),
        })}`
    );
}

function serializeAttributeFilters(selectedItems) {
    if (!selectedItems) return "";
    const cleaned = Object.entries(selectedItems).reduce((acc, [key, vals]) => {
        if (Array.isArray(vals) && vals.length > 0) acc[key] = vals;
        return acc;
    }, {});
    return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : "";
}

// KPIs de Estado de Resultados (tab KPIs, filtro central Ventas/Márgenes/
// Personas/Terceros). Devuelve las series de los 4 gráficos del grupo, con
// ambas variantes de cada toggle (YoY/Recurrentes/$) para alternar sin refetch.
export function getKpisResultados(initDate, endDate, group = "ventas", period = "month") {
    return request.get(
        `${BASE}/kpis-resultados/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            group,
            period,
        })}`
    );
}
